import { fetchBaseQuery, createApi, FetchArgs, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { auth } from './firebase';

// Prevent multiple redirect loops when many requests return 401 at once.
let hasRedirectedToLogin = false;

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_MAIN_URL,
  prepareHeaders: headers => {
    // Use the stored Firebase token
    const token = localStorage.getItem('firebase_id_token');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, unknown> = async (
  args,
  api,
  extraOptions
) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    try {
      // Force token refresh
      const newToken = await auth.currentUser?.getIdToken(true);
      if (newToken) {
        localStorage.setItem('firebase_id_token', newToken);
        result = await baseQuery(args, api, extraOptions);
      } else {
        // Remove any stale token and redirect to login once
        localStorage.removeItem('firebase_id_token');
        if (!hasRedirectedToLogin) {
          hasRedirectedToLogin = true;
          window.location.href = '/login';
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      localStorage.removeItem('firebase_id_token');
      if (!hasRedirectedToLogin) {
        hasRedirectedToLogin = true;
        window.location.href = '/login';
      }
    }
  }

  return result;
};
export const dicomApi = createApi({
  reducerPath: 'dicomApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['EncephalonStudy', 'EncephalonScan'], // Add EncephalonScan
  endpoints: builder => ({
    getDicomData: builder.query<any, string>({
      query: icid => `/api/v1/dicoms/${icid}`,
      extraOptions: { maxRetries: 2 },
    }),
    getStudyData: builder.query<any, string>({
      query: studyId => `/api/v1/studies/${studyId}`,
      extraOptions: { maxRetries: 2 },
    }),
    // media data is now returned as part of the dicom retrieve API (dicom -> media_signed_url / media_uuid)
    // removed standalone getMediaData endpoint to avoid duplicate fetches
    getDicomsInStudy: builder.query<any, string>({
      query: studyId => `/api/v1/dicoms/study/${studyId}`,
      extraOptions: { maxRetries: 2 },
    }),
    getDicomDownloadUrlsForStudy: builder.query<
      Array<{ icid: string; signed_url: string | null; filename: string }>,
      string
    >({
      query: studyId => `/api/v1/dicoms/study/${studyId}/download-urls`,
      extraOptions: { maxRetries: 1 },
    }),
    getDicomsByStudyIcid: builder.query<any, string>({
      query: studyIcid => `/api/v1/dicoms/study_icid/${studyIcid}`,
      extraOptions: { maxRetries: 2 },
    }),
    getReportData: builder.query<any, string>({
      query: study_id => `/api/v1/reports/study/${study_id}`,
      extraOptions: { maxRetries: 2 },
    }),
    getSegmentationLabels: builder.query<any, string>({
      query: mediaUuid => `/api/v1/media/${mediaUuid}/segmentation-labels`,
    }),
    getSegmentationReferences: builder.query<any, string>({
      query: mediaUuid => `/api/v1/media/${mediaUuid}/segmentation-references`,
    }),
    getKeypointLabels: builder.query<import('types/labels').KeypointCollectionLabel[], string>({
      query: mediaUuid => `/api/v1/media/${mediaUuid}/keypoint-collection-labels`,
    }),
    getPerspectives: builder.query<any, number>({
      query: dicomId => `/api/v1/viewlabels/dicom/${dicomId}`,
    }),
    getViewPredictions: builder.query<any, number>({
      query: dicomId => `/api/v1/viewpredictions/dicom/${dicomId}`,
    }),
    getQualityPredictions: builder.query<any, number>({
      query: uuid => `/api/v1/quality_predictions/dicom/${uuid}`,
    }),
    getSegmentationClasses: builder.query<any, string>({
      query: studyId => `/api/v1/studies/${studyId}/segmentation_classes`,
    }),
    getEncephaloneStudyData: builder.query<any, string>({
      query: name => `/api/v1/encephalon-study/${name}`,
      extraOptions: { maxRetries: 2 },
    }),
    getEncephalonScan: builder.query<any, string>({
      query: studyId => `/api/v1/scans/?study_id=${studyId}`,
      providesTags: ['EncephalonScan'],
      transformResponse: (response: any) => {
        return {
          results: response.results || [],
          count: response.count || 0,
        };
      },
      extraOptions: { maxRetries: 2 },
    }),
    createEncephalonStudy: builder.mutation<
      any,
      { mrn: string; age: number; height: number; weight: number; sex: string }
    >({
      query: payload => ({
        url: '/api/v1/encephalon-create-study',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'EncephalonStudy' }],
    }),

    createEncephalonScan: builder.mutation<any, { study_uuid: string; product: string }>({
      query: ({ study_uuid, product }) => ({
        url: `/api/v1/encephalon-create-scan?study_uuid=${study_uuid}&product=${product}`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'EncephalonStudy' }],
    }),
    uploadDicomsSequential: builder.mutation<
      any,
      {
        study_id: string;
        dicom_data: Array<{
          signed_url: string;
          name: string;
        }>;
      }
    >({
      query: payload => ({
        url: '/api/v1/encephalon-upload-dicoms',
        method: 'POST',
        body: payload,
      }),
    }),
    reextractFrames: builder.mutation<any, string>({
      query: icid => ({
        url: `/api/v1/dicoms/${icid}/reextract-frames`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetDicomDataQuery,
  useGetStudyDataQuery,
  useGetDicomsInStudyQuery,
  useGetDicomDownloadUrlsForStudyQuery,
  useGetReportDataQuery,
  useGetSegmentationLabelsQuery,
  useGetKeypointLabelsQuery,
  useGetPerspectivesQuery,
  useGetViewPredictionsQuery,
  useGetQualityPredictionsQuery,
  useGetSegmentationClassesQuery,
  useCreateEncephalonStudyMutation,
  useGetEncephaloneStudyDataQuery,
  useCreateEncephalonScanMutation,
  useGetEncephalonScanQuery,
  useUploadDicomsSequentialMutation,
  useReextractFramesMutation,
  useGetDicomsByStudyIcidQuery,
  useGetSegmentationReferencesQuery,
} = dicomApi;
